import { useState, useEffect, useCallback } from 'react';

const DEFAULT_AVATAR = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="50" fill="%23e2e8f0"/%3E%3Ccircle cx="50" cy="38" r="16" fill="%2394a3b8"/%3E%3Cellipse cx="50" cy="75" rx="28" ry="20" fill="%2394a3b8"/%3E%3C/svg%3E';
const DEFAULT_COVER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 200"%3E%3Crect width="400" height="200" fill="%23cbd5e1"/%3E%3C/svg%3E';

function buildPlatformUrl(platform) {
  const { input, base_url, path } = platform;
  if (!path) return '#';
  if (input === 'email') return `mailto:${path}`;
  if (input === 'phone') return `tel:${path}`;
  if (input === 'username' && base_url) return `${base_url.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  if (input === 'document') return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (base_url) return `${base_url.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  return `https://${path}`;
}

// Shared glass card style
const glassCard = {
  background: "rgba(255,255,255,.70)",
  backdropFilter: "blur(32px) saturate(200%)",
  WebkitBackdropFilter: "blur(32px) saturate(200%)",
  border: "1px solid rgba(255,255,255,.90)",
  borderRadius: 18,
  boxShadow: "0 8px 32px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.96)",
};

export default function ProfileView({ profile, platforms, apiBase, backendUrl, leadType = 2 }) {
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', company: '', job_title: '', country: '', note: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(() => {
    try {
      const stored = localStorage.getItem(`gotaps_lead_${profile.id}`);
      if (!stored) return false;
      const { ts } = JSON.parse(stored);
      // Expire after 30 days
      if (Date.now() - ts > 30 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(`gotaps_lead_${profile.id}`);
        return false;
      }
      return true;
    } catch { return false; }
  });
  const [leadError, setLeadError] = useState('');
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadDismissable, setLeadDismissable] = useState(false);
  const [vis, setVis] = useState(false);
  const [tapCount, setTapCount] = useState(4572);
  const [activeAv, setActiveAv] = useState(0);

  useEffect(() => { setTimeout(() => setVis(true), 80); }, []);
  useEffect(() => {
    const id = setInterval(() => setTapCount(n => n + Math.floor(Math.random() * 3 + 1)), 2600);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setActiveAv(n => (n + 1) % 6), 3000);
    return () => clearInterval(id);
  }, []);

  const AVATARS = [
    { i: "JK", g: "linear-gradient(135deg,#0891b2,#06b6d4)", name: "Jake Kim", co: "Accenture", leads: 84, score: 92 },
    { i: "AR", g: "linear-gradient(135deg,#7c3aed,#a78bfa)", name: "Ana Rivera", co: "Deloitte", leads: 71, score: 88 },
    { i: "ML", g: "linear-gradient(135deg,#059669,#34d399)", name: "Maria Lopez", co: "Salesforce", leads: 96, score: 96 },
    { i: "TC", g: "linear-gradient(135deg,#d97706,#fbbf24)", name: "Tom Chen", co: "Stripe", leads: 58, score: 81 },
    { i: "SL", g: "linear-gradient(135deg,#dc2626,#f87171)", name: "Sara Lin", co: "HubSpot", leads: 63, score: 85 },
    { i: "BN", g: "linear-gradient(135deg,#475569,#94a3b8)", name: "Ben Nash", co: "Microsoft", leads: 77, score: 90 },
  ];

  const displayName = profile.name || profile.username;
  const jobLine = [profile.job_title, profile.company].filter(Boolean).join(' at ');

  const regularPlatforms = platforms.filter(p => p.input !== 'youtubevideo');
  const videoPlatforms = platforms.filter(p => p.input === 'youtubevideo');

  const handlePlatformClick = useCallback((platform) => {
    if (profile.private) return;
    const url = buildPlatformUrl(platform);
    fetch(`${apiBase}/public/profile/${profile.id}/platform-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform_id: platform.platform_id }),
    }).catch(() => {});
    if (platform.input === 'email' || platform.input === 'phone') {
      window.location.href = url;
    } else {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) window.location.href = url;
      else window.open(url, '_blank', 'noopener');
    }
  }, [profile, apiBase]);

  const handleConnect = useCallback(() => {
    window.open(`${apiBase}/public/profile/${profile.id}/vcard`, '_blank');
  }, [profile, apiBase]);

  const handleLeadSubmit = useCallback((e) => {
    e.preventDefault();
    if (!leadForm.name.trim() || !leadForm.email.trim() || !leadForm.phone.trim()) {
      setLeadError('Please fill in all fields.');
      return;
    }
    setLeadSubmitting(true);
    setLeadError('');
    const submit = (lat, lng) => {
      fetch(`${apiBase}/public/profile/${profile.id}/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...leadForm, latitude: lat, longitude: lng, type: leadType }),
      })
        .then(res => { if (!res.ok) throw new Error('Failed'); return res.json(); })
        .then(() => {
          try { localStorage.setItem(`gotaps_lead_${profile.id}`, JSON.stringify({ ts: Date.now() })); } catch {}
          setLeadSubmitted(true);
          setLeadModalOpen(false);
          setLeadForm({ name: '', email: '', phone: '', company: '', job_title: '', country: '', note: '' });
        })
        .catch(() => setLeadError('Something went wrong. Please try again.'))
        .finally(() => setLeadSubmitting(false));
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => submit(pos.coords.latitude, pos.coords.longitude),
        () => submit(null, null), { timeout: 5000 }
      );
    } else { submit(null, null); }
  }, [leadForm, profile, apiBase]);

  const getEmbedUrl = (url) => {
    if (!url) return null;
    let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    m = url.match(/vimeo\.com\/(\d+)/);
    if (m) return `https://player.vimeo.com/video/${m[1]}`;
    return null;
  };

  // Build initials from profile name for use in cards
  const initials = (displayName || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const platformCount = platforms.length;

  return (
    <>
      <style>{`
        @keyframes orbDrift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(38px,-28px) scale(1.07)}}
        @keyframes orbDrift2{0%,100%{transform:translate(0,0)}50%{transform:translate(-32px,22px) scale(1.05)}}
        @keyframes orbDrift3{0%,100%{transform:translate(0,0)}50%{transform:translate(22px,18px)}}
        @keyframes fa{0%,100%{transform:translateY(0px) rotate(-5deg)}50%{transform:translateY(-10px) rotate(-5deg)}}
        @keyframes fb{0%,100%{transform:translateY(0px) rotate(4deg)}50%{transform:translateY(-13px) rotate(4deg)}}
        @keyframes fc{0%,100%{transform:translateY(0px) rotate(-3deg)}50%{transform:translateY(-8px) rotate(-3deg)}}
        @keyframes fd{0%,100%{transform:translateY(0px) rotate(6deg)}50%{transform:translateY(-11px) rotate(6deg)}}
        @keyframes fe{0%,100%{transform:translateY(0px) rotate(-4deg)}50%{transform:translateY(-9px) rotate(-4deg)}}
        @keyframes ff{0%,100%{transform:translateY(0px) rotate(5deg)}50%{transform:translateY(-12px) rotate(5deg)}}
        @keyframes fg{0%,100%{transform:translateY(0px) rotate(-2deg)}50%{transform:translateY(-7px) rotate(-2deg)}}
        @keyframes fh{0%,100%{transform:translateY(0px) rotate(3deg)}50%{transform:translateY(-10px) rotate(3deg)}}
        @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.85)}}
        @keyframes tickUp{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}
        .vp-float-card{transition:transform .22s ease,box-shadow .22s ease}
        .vp-float-card:hover{transform:translateY(-3px) scale(1.01)!important;box-shadow:0 16px 40px rgba(0,0,0,.12),inset 0 1px 0 rgba(255,255,255,.95)!important}
      `}</style>

      <div className="vp-bg">
        {/* Background layers */}
        <div className="vp-scene">
          <div className="vp-orb vp-orb-1" />
          <div className="vp-orb vp-orb-2" />
          <div className="vp-orb vp-orb-3" />
          <div className="vp-orb vp-orb-4" />
          <div className="vp-noise" />
        </div>

        {/* ══ FLOATING CARDS ══ */}

        {/* Card 1 · Live Taps Counter — top-left */}
        <div className="vp-float-card" style={{
          ...glassCard, position: "fixed", top: "5%", left: "2%",
          width: 230, zIndex: 6, padding: "17px 19px",
          opacity: vis ? 1 : 0, transform: vis ? "rotate(-4deg)" : "translateY(18px) rotate(-4deg)",
          transition: "opacity .6s ease .08s, transform .6s cubic-bezier(.16,1,.3,1) .08s",
          animation: "fa 8.5s ease-in-out infinite 0s",
        }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 9 }}>Live Taps · Right Now</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 9, marginBottom: 9 }}>
            <div key={tapCount} style={{ fontSize: 34, fontWeight: 860, color: "#0f172a", letterSpacing: "-2.5px", lineHeight: 1, animation: "tickUp .3s cubic-bezier(.16,1,.3,1)" }}>{tapCount.toLocaleString()}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 8px #22c55e", animation: "pulse 1.8s ease-in-out infinite" }} />
              <span style={{ fontSize: 11, fontWeight: 660, color: "#22c55e" }}>live</span>
            </div>
          </div>
          <div style={{ height: 5, borderRadius: 3, background: "rgba(0,0,0,.06)", overflow: "hidden", marginBottom: 6 }}>
            <div style={{ height: "100%", width: "76%", background: "linear-gradient(90deg,#0891b2,#06b6d4)", borderRadius: 3 }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 9.5, color: "#94a3b8" }}>Target: 6,000</span>
            <span style={{ fontSize: 9.5, fontWeight: 660, color: "#059669" }}>↑ 23%</span>
          </div>
        </div>

        {/* Card 2 · Profile Stats — top-right */}
        <div className="vp-float-card" style={{
          ...glassCard, position: "fixed", top: "5%", left: "65%",
          width: 222, zIndex: 6, padding: "13px 16px",
          opacity: vis ? 1 : 0, transform: vis ? "rotate(5deg)" : "translateY(18px) rotate(5deg)",
          transition: "opacity .6s ease .18s, transform .6s cubic-bezier(.16,1,.3,1) .18s",
          animation: "fb 10s ease-in-out infinite .5s",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", flexShrink: 0 }}>
              {AVATARS.slice(0, 5).map((av, j) => (
                <div key={j} style={{ width: 26, height: 26, borderRadius: "50%", background: av.g, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8.5, fontWeight: 800, color: "#fff", border: "2px solid rgba(255,255,255,.95)", marginLeft: j ? -9 : 0, zIndex: 5 - j, flexShrink: 0, boxShadow: "0 2px 6px rgba(0,0,0,.12)" }}>{av.i}</div>
              ))}
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(100,116,139,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7.5, fontWeight: 700, color: "#64748b", border: "2px solid rgba(255,255,255,.95)", marginLeft: -9, flexShrink: 0 }}>+2k</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>4,200+ teams joined</div>
              <div style={{ display: "flex", alignItems: "center", gap: 2, marginTop: 3 }}>
                {[0, 1, 2, 3, 4].map(s => <span key={s} style={{ fontSize: 9.5, color: "#f59e0b" }}>★</span>)}
                <span style={{ fontSize: 9, color: "#94a3b8", marginLeft: 3 }}>5.0 · 2.4k</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3 · NFC Tap notification with profile name */}
        <div className="vp-float-card" style={{
          ...glassCard, position: "fixed", top: "40%", left: "72%",
          width: 248, zIndex: 6, padding: "13px 16px",
          opacity: vis ? 1 : 0, transform: vis ? "rotate(-6deg)" : "translateY(18px) rotate(-6deg)",
          transition: "opacity .6s ease .28s, transform .6s cubic-bezier(.16,1,.3,1) .28s",
          animation: "fc 9s ease-in-out infinite .8s",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: "linear-gradient(135deg,#0891b2,#06b6d4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0, boxShadow: "0 4px 12px rgba(8,145,178,.28)" }}>💳</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 720, color: "#0f172a" }}>NFC Tap — New Lead</div>
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>via {displayName}'s card · now</div>
            </div>
            <div style={{ fontSize: 9.5, fontWeight: 660, color: "#059669", background: "rgba(5,150,105,.09)", padding: "2px 7px", borderRadius: 20, border: "1px solid rgba(5,150,105,.14)", flexShrink: 0 }}>AI 99</div>
          </div>
        </div>

        {/* Card 4 · Top Performer */}
        <div className="vp-float-card" style={{
          ...glassCard, position: "fixed", top: "18%", right: "2%",
          width: 240, zIndex: 6, padding: "16px 18px",
          opacity: vis ? 1 : 0, transform: vis ? "rotate(4deg)" : "translateY(18px) rotate(4deg)",
          transition: "opacity .6s ease .22s, transform .6s cubic-bezier(.16,1,.3,1) .22s",
          animation: "fd 9.5s ease-in-out infinite .3s",
        }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 11 }}>🏆 Top Performer</div>
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 11 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: AVATARS[activeAv].g, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff", flexShrink: 0, boxShadow: "0 4px 12px rgba(0,0,0,.18)", transition: "background .6s ease" }}>{AVATARS[activeAv].i}</div>
            <div style={{ transition: "all .4s ease", minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 730, color: "#0f172a", letterSpacing: "-.2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{AVATARS[activeAv].name}</div>
              <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 2 }}>{AVATARS[activeAv].co}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 7 }}>
            {[["Leads", AVATARS[activeAv].leads, "#dc2626"], ["Score", AVATARS[activeAv].score, "#7c3aed"], ["Taps", "1.2k", "#0891b2"]].map(([l, v, c]) => (
              <div key={l} style={{ flex: 1, textAlign: "center", padding: "6px 3px", borderRadius: 8, background: `${c}09`, border: `1px solid ${c}18` }}>
                <div style={{ fontSize: 13, fontWeight: 780, color: c, letterSpacing: "-.5px", transition: "all .4s" }}>{v}</div>
                <div style={{ fontSize: 8.5, color: "#94a3b8", marginTop: 2, fontWeight: 600 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 5 · Collect 3x More Leads — lower-left */}
        <div className="vp-float-card" style={{
          ...glassCard, position: "fixed", top: "70%", left: "2%",
          width: 224, zIndex: 6, padding: "13px 16px",
          border: "1px solid rgba(124,58,237,.18)",
          opacity: vis ? 1 : 0, transform: vis ? "rotate(-3deg)" : "translateY(18px) rotate(-3deg)",
          transition: "opacity .6s ease .38s, transform .6s cubic-bezier(.16,1,.3,1) .38s",
          animation: "fe 11s ease-in-out infinite 1.2s",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: 7, background: "rgba(124,58,237,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>✦</div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed" }}>AI Insight</span>
          </div>
          <div style={{ fontSize: 10.5, color: "#475569", lineHeight: 1.65 }}>Collect <b style={{ color: "#0f172a" }}>3x more leads</b> with NFC digital cards. Best window: <b style={{ color: "#0f172a" }}>Tue–Thu 10am</b></div>
        </div>

        {/* Card 6 · Profile Links Summary — lower-right */}
        <div className="vp-float-card" style={{
          ...glassCard, position: "fixed", top: "52%", right: "2%",
          width: 228, zIndex: 6, padding: "13px 16px",
          opacity: vis ? 1 : 0, transform: vis ? "rotate(6deg)" : "translateY(18px) rotate(6deg)",
          transition: "opacity .6s ease .32s, transform .6s cubic-bezier(.16,1,.3,1) .32s",
          animation: "ff 10.5s ease-in-out infinite .7s",
        }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 9 }}>{displayName}'s Profile</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 7px #22c55e", animation: "pulse 2s infinite", flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 640, color: "#0f172a" }}>{platformCount} links · Active now</span>
          </div>
          <div style={{ display: "flex", gap: 7 }}>
            {[["Links", platformCount, "#0891b2"], ["CRM", "Sync", "#059669"], ["NFC", "Ready", "#7c3aed"]].map(([l, v, c]) => (
              <div key={l} style={{ flex: 1, textAlign: "center", padding: "5px 3px", borderRadius: 8, background: `${c}09`, border: `1px solid ${c}18` }}>
                <div style={{ fontSize: 11, fontWeight: 780, color: c }}>{v}</div>
                <div style={{ fontSize: 8, color: "#94a3b8", marginTop: 2, fontWeight: 600 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 7 · GoTaps Benefits — bottom-left */}
        <div className="vp-float-card" style={{
          ...glassCard, position: "fixed", bottom: "3%", left: "30%",
          width: 218, zIndex: 6, padding: "13px 15px",
          opacity: vis ? 1 : 0, transform: vis ? "rotate(-5deg)" : "translateY(18px) rotate(-5deg)",
          transition: "opacity .6s ease .44s, transform .6s cubic-bezier(.16,1,.3,1) .44s",
          animation: "fg 12s ease-in-out infinite .2s",
        }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 9 }}>Why GoTaps</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {[
              { ico: "💳", text: "NFC Digital Cards", sub: "Tap & share instantly" },
              { ico: "📊", text: "Lead Capture", sub: "Collect 3x more leads" },
              { ico: "🔗", text: "CRM Sync", sub: "Auto-sync contacts" },
            ].map((item, j) => (
              <div key={j} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(8,145,178,.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>{item.ico}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 640, color: "#0f172a" }}>{item.text}</div>
                  <div style={{ fontSize: 9, color: "#94a3b8" }}>{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 8 · New Connection — top-center */}
        <div className="vp-float-card" style={{
          ...glassCard, position: "fixed", top: "3%", left: "28%",
          width: 220, zIndex: 6, padding: "13px 16px",
          opacity: vis ? 1 : 0, transform: vis ? "rotate(4deg)" : "translateY(18px) rotate(4deg)",
          transition: "opacity .6s ease .50s, transform .6s cubic-bezier(.16,1,.3,1) .50s",
          animation: "fh 8.5s ease-in-out infinite 1.0s",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#059669,#34d399)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0, boxShadow: "0 4px 10px rgba(5,150,105,.26)" }}>🤝</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "#0f172a" }}>New Connection</div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>Someone saved {displayName}'s card</div>
            </div>
            <div style={{ fontSize: 9.5, color: "#94a3b8", flexShrink: 0, marginLeft: 4 }}>2m ago</div>
          </div>
        </div>

        {/* ══ CENTER PANEL — full height, scrollable ══ */}
        <div className="vp-center">
          <div className="vp-panel">
            {/* Top banner — animated dark gradient background, static text */}
            <a href="https://www.gotaps.me" target="_blank" rel="noopener noreferrer" className="vp-top-banner">
              <span>Tap here to get your GoTaps profile</span>
            </a>

            <div className="vp-scroll">

              {/* Cover */}
              <div className="vp-cover-wrap">
                <img
                  className="vp-cover"
                  src={profile.cover_photo || DEFAULT_COVER}
                  alt="Cover"
                  style={!profile.cover_photo && profile.cover_color ? { background: profile.cover_color } : undefined}
                  onError={e => { e.target.src = DEFAULT_COVER; }}
                />
              </div>

              {/* Avatar */}
              <div className="vp-avatar-wrap">
                <img
                  className="vp-avatar"
                  src={profile.photo || DEFAULT_AVATAR}
                  alt={displayName}
                  onError={e => { e.target.src = DEFAULT_AVATAR; }}
                />
              </div>

              {/* Info */}
              <div className="vp-info">
                <div className="vp-name-row">
                  <h1 className="vp-name">{displayName}</h1>
                  {profile.verified && (
                    <span className="vp-verified">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#fff" />
                      </svg>
                    </span>
                  )}
                </div>
                {jobLine && <p className="vp-job">{jobLine}</p>}
                {profile.bio && <p className="vp-bio">{profile.bio}</p>}
              </div>

              <hr className="vp-divider" />

              {/* Save to Contact button */}
              <div className="vp-action-section">
                <button className="vp-connect-btn" onClick={handleConnect} disabled={profile.private} style={profile.private ? { opacity: 0.3, cursor: 'not-allowed' } : undefined}>
                  Save to Contact
                </button>
              </div>

              {/* Platforms grid — always shown */}
              {regularPlatforms.length > 0 && (
                <div className="vp-platforms">
                  <div className="vp-platform-grid">
                    {regularPlatforms.map((p, i) => (
                      <button
                        key={`${p.platform_id}-${i}`}
                        className={`vp-platform-item${profile.private ? ' vp-blurred' : ''}`}
                        onClick={() => handlePlatformClick(p)}
                        disabled={profile.private}
                      >
                        <div className="vp-platform-icon-wrap">
                          <img
                            className="vp-platform-icon"
                            src={p.icon || DEFAULT_AVATAR}
                            alt={p.label || p.title}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        <span className="vp-platform-label">
                          {(p.label && p.label.trim()) ? p.label : p.title}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* YouTube / Vimeo embeds */}
              {!profile.private && videoPlatforms.map((vp, i) => {
                const embedUrl = getEmbedUrl(vp.path);
                if (!embedUrl) return null;
                return (
                  <div className="vp-video-wrap" key={`video-${i}`}>
                    <div className="vp-video-responsive">
                      <iframe
                        src={embedUrl}
                        title={vp.label || vp.title}
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                  </div>
                );
              })}

              {/* Share Your Info button — reopens lead modal */}
              {profile.is_leads_enabled && (
                <div className="vp-action-section">
                  <button className="vp-share-info-btn" onClick={() => { setLeadModalOpen(true); setLeadDismissable(true); }}>
                    Share Your Info
                  </button>
                </div>
              )}

              {/* Bottom spacer for phones that cut off bottom */}
              <div style={{ height: 100, flexShrink: 0 }} />

            </div>
          </div>
        </div>

        {/* ══ LEAD CAPTURE MODAL — glassmorphic ══ */}
        {profile.is_leads_enabled && (!leadSubmitted || leadModalOpen) && (
          <div className="vp-modal-overlay">
            <div className="vp-modal-card">
              {/* Close button — only when reopened via Share Your Info */}
              {leadDismissable && (
                <button className="vp-modal-close" onClick={() => { setLeadModalOpen(false); }} aria-label="Close">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              {/* Profile photo at top */}
              <div className="vp-modal-avatar-wrap">
                <img
                  className="vp-modal-avatar"
                  src={profile.photo || DEFAULT_AVATAR}
                  alt={displayName}
                  onError={e => { e.target.src = DEFAULT_AVATAR; }}
                />
              </div>
              <h2 className="vp-modal-title">Share your info with {displayName}</h2>

              <form className="vp-lead-form" onSubmit={handleLeadSubmit}>
                <input className="vp-lead-input" type="text" placeholder="Full Name *" value={leadForm.name} onChange={e => setLeadForm(f => ({ ...f, name: e.target.value }))} required />
                <input className="vp-lead-input" type="email" placeholder="Email *" value={leadForm.email} onChange={e => setLeadForm(f => ({ ...f, email: e.target.value }))} required />
                <input className="vp-lead-input" type="tel" placeholder="Phone Number *" inputMode="numeric" pattern="[0-9+\-\s()]*" value={leadForm.phone} onChange={e => setLeadForm(f => ({ ...f, phone: e.target.value.replace(/[^0-9+\-\s()]/g, '') }))} required />
                <input className="vp-lead-input" type="text" placeholder="Company" value={leadForm.company} onChange={e => setLeadForm(f => ({ ...f, company: e.target.value }))} />
                <input className="vp-lead-input" type="text" placeholder="Job Title" value={leadForm.job_title} onChange={e => setLeadForm(f => ({ ...f, job_title: e.target.value }))} />
                <input className="vp-lead-input" type="text" placeholder="Country" value={leadForm.country} onChange={e => setLeadForm(f => ({ ...f, country: e.target.value }))} />
                <textarea className="vp-lead-input vp-lead-textarea" placeholder="Note" value={leadForm.note} onChange={e => setLeadForm(f => ({ ...f, note: e.target.value }))} rows={3} />
                {leadError && <p className="vp-lead-error">{leadError}</p>}
                <button type="submit" className="vp-connect-btn" disabled={leadSubmitting}>
                  {leadSubmitting ? 'Sending...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
